/**
 * Tiny observable store.
 *
 * One immutable state object, one notification channel. Components subscribe,
 * receive the whole state and re-render themselves. That is fast enough for a
 * single-week scheduling tool and keeps every component independent of every
 * other - which is what lets them be developed and tested in isolation.
 */

/**
 * @param {object} initialState
 * @returns {{ getState: Function, setState: Function, subscribe: Function, select: Function }}
 */
export function createStore(initialState) {
  let state = initialState;
  const listeners = new Set();
  let notifying = false;
  let pendingNotify = false;

  function notify(meta) {
    if (notifying) {
      pendingNotify = true;
      return;
    }
    notifying = true;
    try {
      for (const listener of [...listeners]) {
        try {
          listener(state, meta);
        } catch (error) {
          console.error('[lunchly] store listener failed', error);
        }
      }
    } finally {
      notifying = false;
      if (pendingNotify) {
        pendingNotify = false;
        notify({ reason: 'cascade' });
      }
    }
  }

  return {
    getState() {
      return state;
    },

    /**
     * @param {object|Function} updater - a partial state object, or a function
     *   receiving the current state and returning a partial (or a whole new
     *   state object).
     * @param {object} [meta] - describes the change, e.g. `{ reason: 'generate' }`.
     *   Listeners may use it to skip expensive work.
     */
    setState(updater, meta = {}) {
      const patch = typeof updater === 'function' ? updater(state) : updater;
      if (!patch) return state;
      const next = { ...state, ...patch };
      if (next === state) return state;
      state = next;
      notify(meta);
      return state;
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /** Subscribe to a derived slice; the listener only fires when it changes. */
    select(selector, listener) {
      let previous = selector(state);
      return this.subscribe((next, meta) => {
        const current = selector(next);
        if (current !== previous) {
          previous = current;
          listener(current, meta);
        }
      });
    },
  };
}
